import React, { useState } from 'react';

const SendAlertModal = () => {
  const [alertData, setAlertData] = useState({
    recipients: 'all',
    category: 'general',
    priority: 'medium',
    subject: '',
    message: '',
    sendSMS: false,
    sendEmail: true,
    selectedStudents: [] as string[]
  });

  // Mock student data
  const students = [
    { id: '1', name: 'John Smith', rollNo: '001', parentEmail: 'john.parent@email.com' },
    { id: '2', name: 'Sarah Johnson', rollNo: '002', parentEmail: 'sarah.parent@email.com' },
    { id: '3', name: 'Mike Wilson', rollNo: '003', parentEmail: 'mike.parent@email.com' },
    { id: '4', name: 'Emily Davis', rollNo: '004', parentEmail: 'emily.parent@email.com' }
  ];

  const alertCategories = [
    { value: 'academic', label: 'Academic Performance' },
    { value: 'behavior', label: 'Behavior' },
    { value: 'attendance', label: 'Attendance' },
    { value: 'homework', label: 'Homework' },
    { value: 'general', label: 'General' },
    { value: 'emergency', label: 'Emergency' },
    { value: 'event', label: 'School Event' }
  ];

  const messageTemplates = {
    academic: 'Your child has shown excellent progress in {subject}. Keep up the good work!',
    behavior: 'I wanted to discuss your child\'s behavior in class today. Please contact me at your convenience.',
    attendance: 'Your child was absent from class today. Please ensure regular attendance.',
    homework: 'Your child has not submitted homework for {subject}. Please help them catch up.',
    general: 'I wanted to update you about your child\'s progress in class.',
    emergency: 'This is an urgent matter regarding your child. Please contact the school immediately.',
    event: 'Don\'t forget about the upcoming school event on {date}. More details attached.'
  };

  const handleTemplateSelect = (category: string) => {
    const template = messageTemplates[category as keyof typeof messageTemplates];
    if (template) {
      setAlertData({...alertData, message: template});
    }
  };

  const handleStudentSelection = (studentId: string, isSelected: boolean) => {
    if (isSelected) {
      setAlertData({
        ...alertData, 
        selectedStudents: [...alertData.selectedStudents, studentId]
      });
    } else {
      setAlertData({
        ...alertData, 
        selectedStudents: alertData.selectedStudents.filter(id => id !== studentId)
      });
    }
  };

  const handleSubmit = () => {
    // TODO: Send alert API call
    const recipients = alertData.recipients === 'all' ? 
      students.map(s => s.id) : 
      alertData.selectedStudents;
    
    console.log('Sending alert:', {
      ...alertData,
      recipients
    });
  };

  const getRecipientCount = () => {
    return alertData.recipients === 'all' ? 
      students.length : 
      alertData.selectedStudents.length;
  };

  return (
    <div className="modal fade" id="sendAlertModal">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Send Alert to Parents</h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div className="modal-body">
            <form>
              {/* Recipients */}
              <div className="mb-3">
                <label className="form-label">Recipients <span className="text-danger">*</span></label>
                <div className="form-check">
                  <input 
                    className="form-check-input" 
                    type="radio" 
                    name="recipients" 
                    id="allParents"
                    checked={alertData.recipients === 'all'}
                    onChange={() => setAlertData({...alertData, recipients: 'all'})}
                  />
                  <label className="form-check-label" htmlFor="allParents">
                    All parents in my classes ({students.length} recipients)
                  </label>
                </div>
                <div className="form-check">
                  <input 
                    className="form-check-input" 
                    type="radio" 
                    name="recipients" 
                    id="selectedParents"
                    checked={alertData.recipients === 'selected'}
                    onChange={() => setAlertData({...alertData, recipients: 'selected'})}
                  />
                  <label className="form-check-label" htmlFor="selectedParents">
                    Selected parents
                  </label>
                </div>
              </div>

              {/* Student Selection */}
              {alertData.recipients === 'selected' && (
                <div className="mb-3">
                  <label className="form-label">Select Students</label>
                  <div className="border rounded p-3" style={{maxHeight: '200px', overflowY: 'auto'}}>
                    {students.map((student) => (
                      <div key={student.id} className="form-check">
                        <input 
                          className="form-check-input" 
                          type="checkbox" 
                          id={`student-${student.id}`}
                          checked={alertData.selectedStudents.includes(student.id)}
                          onChange={(e) => handleStudentSelection(student.id, e.target.checked)}
                        />
                        <label className="form-check-label" htmlFor={`student-${student.id}`}>
                          {student.name} (Roll: {student.rollNo})
                        </label>
                      </div>
                    ))}
                  </div>
                  <small className="text-muted">
                    {alertData.selectedStudents.length} of {students.length} students selected
                  </small>
                </div>
              )}

              {/* Alert Details */}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label">Category <span className="text-danger">*</span></label>
                  <select 
                    className="form-select"
                    value={alertData.category}
                    onChange={(e) => {
                      setAlertData({...alertData, category: e.target.value});
                      handleTemplateSelect(e.target.value);
                    }}
                    required
                  >
                    {alertCategories.map((cat) => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6 mb-3">
                  <label className="form-label">Priority</label>
                  <select 
                    className="form-select"
                    value={alertData.priority}
                    onChange={(e) => setAlertData({...alertData, priority: e.target.value})}
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Subject <span className="text-danger">*</span></label>
                <input 
                  type="text" 
                  className="form-control"
                  value={alertData.subject}
                  onChange={(e) => setAlertData({...alertData, subject: e.target.value})}
                  placeholder="Enter alert subject"
                  required
                />
              </div>

              <div className="mb-3">
                <label className="form-label">Message <span className="text-danger">*</span></label>
                <textarea 
                  className="form-control"
                  rows={5}
                  value={alertData.message}
                  onChange={(e) => setAlertData({...alertData, message: e.target.value})}
                  placeholder="Enter your message to parents..."
                  required
                />
                <small className="text-muted">
                  Characters: {alertData.message.length} | Recipients: {getRecipientCount()}
                </small>
              </div>

              {/* Delivery Options */}
              <div className="mb-3">
                <label className="form-label">Delivery Method</label>
                <div className="form-check">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="sendEmail"
                    checked={alertData.sendEmail}
                    onChange={(e) => setAlertData({...alertData, sendEmail: e.target.checked})}
                  />
                  <label className="form-check-label" htmlFor="sendEmail">
                    Send via Email
                  </label>
                </div>
                <div className="form-check">
                  <input 
                    className="form-check-input" 
                    type="checkbox" 
                    id="sendSMS"
                    checked={alertData.sendSMS}
                    onChange={(e) => setAlertData({...alertData, sendSMS: e.target.checked})}
                  />
                  <label className="form-check-label" htmlFor="sendSMS">
                    Send via SMS (additional charges may apply)
                  </label>
                </div>
              </div>

              {/* Preview */}
              {alertData.subject && alertData.message && (
                <div className="alert alert-info">
                  <h6><i className="ti ti-eye me-2"></i>Preview</h6>
                  <strong>Subject:</strong> {alertData.subject}<br/>
                  <strong>Message:</strong> {alertData.message}<br/>
                  <strong>Recipients:</strong> {getRecipientCount()} parent(s)
                </div>
              )}
            </form>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-cancel" data-bs-dismiss="modal">
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={handleSubmit}
              disabled={!alertData.subject || !alertData.message || getRecipientCount() === 0}
            >
              <i className="ti ti-send me-2"></i>Send Alert
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SendAlertModal;
